import {
  type CandlestickData,
  type Time,
  type DeepPartial,
  type SolidColor,
  type GridLineOptions,
  type PriceScaleOptions,
  type TimeScaleOptions,
  type LayoutOptions,
  type ChartOptions,
  type CandlestickStyleOptions,
  type HistogramStyleOptions,
  type SeriesOptionsCommon,
  type PriceFormatBuiltIn,
} from 'lightweight-charts';

// Chart configuration
export const CHART_OPTIONS: DeepPartial<ChartOptions> = {
  layout: {
    background: { type: 'solid', color: '#ffffff' } as SolidColor,
    textColor: '#333333',
    fontFamily: 'Arial, sans-serif',
    fontSize: 12,
  } as LayoutOptions,
  grid: {
    vertLines: { 
      color: '#f0f0f0',
      style: 1,
      visible: true,
    } as GridLineOptions,
    horzLines: { 
      color: '#f0f0f0',
      style: 1,
      visible: true,
    } as GridLineOptions,
  },
  rightPriceScale: {
    borderColor: '#f0f0f0',
    scaleMargins: {
      top: 0.1,
      bottom: 0.1,
    },
    autoScale: true,
    mode: 0,
    invertScale: false,
    alignLabels: true,
    borderVisible: true,
    entireTextOnly: false,
    ticksVisible: true,
    minimumWidth: 0,
  } as PriceScaleOptions,
  leftPriceScale: {
    visible: false,
    autoScale: true,
    mode: 0,
    invertScale: false,
    alignLabels: true,
    borderVisible: true,
    entireTextOnly: false,
    ticksVisible: true,
    minimumWidth: 0,
  } as PriceScaleOptions,
  timeScale: {
    timeVisible: true,
    rightOffset: 0,
    barSpacing: 6,
    fixLeftEdge: true,
    lockVisibleTimeRangeOnResize: true,
    borderColor: '#f0f0f0',
    visible: true,
    borderVisible: true,
    secondsVisible: false,
    ticksVisible: true,
    fixRightEdge: true,
    minBarSpacing: 0.5,
    rightBarStaysOnScroll: false,
    shiftVisibleRangeOnNewBar: true,
    allowShiftVisibleRangeOnWhitespaceReplacement: true,
    uniformDistribution: false,
    minimumHeight: 0,
  } as TimeScaleOptions,
  crosshair: {
    mode: 1,
    vertLine: {
      color: '#333333',
      width: 1,
      style: 1,
      visible: true,
      labelVisible: true,
      labelBackgroundColor: '#333333',
    },
    horzLine: {
      color: '#333333',
      width: 1,
      style: 1,
      visible: true,
      labelVisible: true,
      labelBackgroundColor: '#333333',
    },
  },
};

// Candlestick series configuration
export const CANDLESTICK_SERIES_OPTIONS: DeepPartial<CandlestickStyleOptions & SeriesOptionsCommon> = {
  upColor: '#26a69a',
  downColor: '#ef5350',
  borderVisible: false,
  wickVisible: true,
  wickUpColor: '#26a69a',
  wickDownColor: '#ef5350',
  lastValueVisible: true,
  title: 'Stock Price',
  visible: true,
  priceLineVisible: false,
  priceFormat: {
    type: 'price' as const,
    precision: 2,
    minMove: 0.01,
  },
};

// Volume series configuration
export const VOLUME_SERIES_OPTIONS: DeepPartial<HistogramStyleOptions & SeriesOptionsCommon> = {
  color: '#26a69a',
  priceFormat: {
    type: 'volume' as const,
  },
  priceScaleId: 'volume',
  lastValueVisible: true,
  visible: true,
};

// Volume scale configuration
export const VOLUME_SCALE_OPTIONS = {
  mode: 2,
  scaleMargins: {
    top: 0.9,
    bottom: 0,
  },
  autoScale: true,
  alignLabels: true,
  borderVisible: true,
};

// Helper function to convert Unix timestamp to lightweight-charts Time format
export const convertToChartTime = (timestamp: number): Time => {
  const date = new Date(timestamp * 1000);
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate()
  };
};

// Helper function to ensure numeric values
export const ensureNumber = (value: string | number): number => {
  return typeof value === 'string' ? parseFloat(value) : value;
};

// Data validator and cleaner for chart data
export const validateAndCleanChartData = (data: any[]): any[] => {
  if (!Array.isArray(data) || data.length === 0) {
    return [];
  }

  // First, normalize all timestamps to numbers and filter out invalid data
  const normalizedData = data
    .map(point => ({
      ...point,
      timestamp: typeof point.timestamp === 'string' ? parseInt(point.timestamp, 10) : point.timestamp,
      open: ensureNumber(point.open),
      high: ensureNumber(point.high),
      low: ensureNumber(point.low),
      close: ensureNumber(point.close),
      volume: ensureNumber(point.volume),
    }))
    .filter(point => 
      // Filter out invalid data points
      !isNaN(point.timestamp) && 
      !isNaN(point.open) && 
      !isNaN(point.high) && 
      !isNaN(point.low) && 
      !isNaN(point.close) && 
      !isNaN(point.volume) &&
      point.timestamp > 0
    );

  // Sort by timestamp in ascending order
  const sortedData = normalizedData.sort((a, b) => a.timestamp - b.timestamp);

  // Remove exact duplicates and keep latest data for same timestamps
  const uniqueData: any[] = [];
  const timestampMap = new Map<number, any>();

  sortedData.forEach(point => {
    const existingPoint = timestampMap.get(point.timestamp);
    if (!existingPoint) {
      // First occurrence of this timestamp
      timestampMap.set(point.timestamp, point);
      uniqueData.push(point);
    } else {
      // Replace existing point with current one (keep latest)
      const existingIndex = uniqueData.findIndex(item => item.timestamp === point.timestamp);
      if (existingIndex >= 0) {
        uniqueData[existingIndex] = point;
        timestampMap.set(point.timestamp, point);
      }
    }
  });

  // Final validation: ensure strict ascending order
  const finalData = uniqueData.sort((a, b) => a.timestamp - b.timestamp);
  
  // Remove any remaining duplicates that might cause chart time conflicts
  const chartTimeMap = new Map<string, any>();
  const validatedData: any[] = [];

  finalData.forEach(point => {
    const chartTime = convertToChartTime(point.timestamp);
    let chartTimeKey: string;
    
    // Handle both object and string time formats
    if (typeof chartTime === 'object' && chartTime !== null) {
      chartTimeKey = `${(chartTime as any).year}-${(chartTime as any).month}-${(chartTime as any).day}`;
    } else {
      chartTimeKey = String(chartTime);
    }
    
    if (!chartTimeMap.has(chartTimeKey)) {
      chartTimeMap.set(chartTimeKey, point);
      validatedData.push(point);
    } else {
      // If there's a conflict at the chart time level, keep the one with later timestamp
      const existing = chartTimeMap.get(chartTimeKey);
      if (point.timestamp > existing!.timestamp) {
        const existingIndex = validatedData.findIndex(item => item.timestamp === existing!.timestamp);
        if (existingIndex >= 0) {
          validatedData[existingIndex] = point;
          chartTimeMap.set(chartTimeKey, point);
        }
      }
    }
  });

  console.log(`Data validation: ${data.length} → ${normalizedData.length} → ${uniqueData.length} → ${validatedData.length} records`);
  return validatedData;
};
